import React, { useContext, useEffect, useMemo, useState } from 'react'
import UserContext from '../contexts/UserContext';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { SubscriptionPlanPrice } from '../models/SubscriptionPlanPrice';
import SnackbarContext from '../contexts/SnackbarContext';
import { postCancelSubscription, reactivateSubscription } from '../config/apiRoutes';
import { RestaurantSubscription } from '../models/RestaurantSubscription';
import useFetch from '../hooks/useFetch';
import SmallButton from '../buttons/SmallButton';
import ConfirmationDialog from '../modal/ConfirmationDialog';
import SectionTitle from '../texts/SectionTitle';
import NoSubscriptionCard from './NoSubscriptionCard';

type Props = {
    prices: SubscriptionPlanPrice[];
}

const CurrentSubscriptionCard = ({ prices }: Props) => {
    const { currentSubscription, selectedRestaurant, refresh } = useContext(UserContext);
    const { t } = useTranslation();
    const [cancelConfirmationOpen, setCancelConfirmationOpen] = useState(false);
    const [reactivateConfirmationOpen, setReactivateConfirmationOpen] = useState(false);
    const cancellationCaller = useFetch();
    const currentSubscriptionPrice = useMemo(() => {
        if (currentSubscription == null || prices.length === 0) return undefined;
        return prices.find(price => price.planId === currentSubscription.subscriptionPlanId && price.interval === currentSubscription.paymentInterval);
    }, [currentSubscription, prices]);
    const { showMessage } = useContext(SnackbarContext);
    async function handleCancelCLick() {
        setCancelConfirmationOpen(true);
    }

    async function handleReactivation() {
        if (currentSubscription == null || selectedRestaurant == null) return;
        const url = reactivateSubscription
            .replace(":restaurantShortName", selectedRestaurant.restaurantShortName)
            .replace(":subscriptionId", currentSubscription.id.toString());
        const response = await cancellationCaller.call<any, RestaurantSubscription>("POST", url);
        if (response.ok && response.data) {
            refresh();
            showMessage(t("payment.reactivate_successful"), "success");
        }
    }

    async function handleCancellation() {
        if (currentSubscription == null || selectedRestaurant == null) return;
        const url = postCancelSubscription
            .replace(":restaurantShortName", selectedRestaurant.restaurantShortName)
            .replace(":subscriptionId", currentSubscription.id.toString());
        const response = await cancellationCaller.call<any, RestaurantSubscription>("POST", url);
        if (response.ok && response.data) {
            refresh();
            showMessage(t("payment.subscription_cancelled"), "success");
        }
    }

    useEffect(() => {
        if (cancellationCaller.errorMessage && cancellationCaller.error) {
            showMessage(cancellationCaller.errorMessage, "error");
        }
    }, [cancellationCaller.errorMessage, cancellationCaller.error]);

    return (
        <div>
            <div className="mt-2">
                <SectionTitle>
                    {t("payment.current_subscription")}
                </SectionTitle>
            </div>
            {currentSubscription == null && (
                <>
                    <NoSubscriptionCard prices={prices} />
                </>
            )}
            {currentSubscription != null && (
                <div className="bg-secondary-pale rounded-[8px] shadow-sm px-2 py-2">
                    <div className="flex flex-row justify-between gap-2">
                    </div>
                    <div className="flex md:flex-row flex-col justify-between gap-2">
                        <div className="text-primary-dark font-bold font-[17px]">
                            {t(`payment.subscription_plans.${currentSubscription.subscriptionPlanId}`)}
                            {currentSubscription.status === "CANCELLED" && (
                                <strong className={`text-error pl-1`}>
                                    ({t(`payment.subscription_status.${currentSubscription.status}`)})
                                </strong>
                            )}
                            <div>
                                {currentSubscription.status === "ACTIVE" && currentSubscription && (
                                    <div>
                                        {currentSubscriptionPrice?.priceGross.toLocaleString()} Ft/{t(`payment.subscription_interval_prices.${currentSubscription.paymentInterval}`)}
                                    </div>
                                )}
                                {currentSubscription.status === "CANCELLED" && (
                                    <div className="text-[14px] font-bold text-primary-dark">
                                        {t("payment.valid_to")}: {format(currentSubscription.validTo, "yyyy-MM-dd")}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div>
                            <div>
                                {currentSubscription.status === "ACTIVE" && (
                                    <SmallButton
                                        fullWidthOnMobile
                                        onClick={handleCancelCLick}>
                                        {t("payment.cancel")}
                                    </SmallButton>
                                )}
                                {currentSubscription.status === "CANCELLED" && (
                                    <SmallButton
                                        fullWidthOnMobile
                                        onClick={() => setReactivateConfirmationOpen(true)}
                                    >
                                        {t("payment.reactivate")}
                                    </SmallButton>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-center items-center mt-6">
                        <img
                            src={process.env.REACT_APP_PUBLIC_URL + '/assets/barion-banner-lightmode.svg'}
                            alt="Barion banner"
                            style={{ maxWidth: '200px', width: '100%' }}
                        />
                    </div>
                </div>
            )}
            <ConfirmationDialog
                open={cancelConfirmationOpen}
                onClose={() => setCancelConfirmationOpen(false)}
                onConfirm={() => handleCancellation()}
                onCancel={() => setCancelConfirmationOpen(false)}
                texts={{
                    title: t("payment.cancel_subscription_confirmation"),
                    confirm: t("payment.cancel"),
                    cancel: t("common.close")
                }}
            >
                {currentSubscription && (
                    <>
                        <div>
                            {t("payment.cancel_subscription_valid_until")}: {format(currentSubscription.validTo, "yyyy-MM-dd")}
                        </div>
                    </>
                )}
            </ConfirmationDialog>
            <ConfirmationDialog
                open={reactivateConfirmationOpen}
                onClose={() => setReactivateConfirmationOpen(false)}
                onConfirm={() => handleReactivation()}
                onCancel={() => setReactivateConfirmationOpen(false)}
                texts={{
                    title: t("payment.reactivate_subscription_confirmation"),
                    confirm: t("payment.reactivate"),
                    cancel: t("common.close")
                }}
            >
                <div>
                    {currentSubscription && (
                        <>
                            {currentSubscription.nextDueDate && (
                                <div>
                                    {t("payment.next_billing_date")}: {format(currentSubscription.nextDueDate, "yyyy-MM-dd")}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </ConfirmationDialog>
        </div>
    )
}

export default CurrentSubscriptionCard